import { NORDIC_REVIEW_REPO, type INordicReviewTeamCoverage } from '@skystone/shared';

/**
 * Team roster cards with PR coverage (PRODOPSAI-679).
 *
 * Every member is listed in full — no truncation, no expand, no hover. There
 * are 25 people across the three teams and the longest list is 12, so there is
 * nothing to collapse, and a hidden list would be one more thing to click on a
 * page that already asks a lot of the reader.
 *
 * Coverage is on the page because the roster is a hand-maintained seed. Showing
 * "x of y" means a low number is legible as scope rather than mistaken for a
 * team having done less work.
 */

function formatShare(count: number, total: number): string {
  if (total === 0) return '0%';
  return `${Math.round((count / total) * 100)}%`;
}

interface INordicReviewTeamCardsProps {
  coverage: INordicReviewTeamCoverage[];
  totalPrCount: number;
}

export function NordicReviewTeamCards({
  coverage,
  totalPrCount,
}: Readonly<INordicReviewTeamCardsProps>) {
  if (coverage.length === 0) {
    return null;
  }

  const attributed = coverage.reduce((sum, team) => sum + team.prCount, 0);
  const memberCount = coverage.reduce((sum, team) => sum + team.members.length, 0);

  return (
    <div className="space-y-2" data-testid="nordic-review-team-cards">
      <h4 className="text-xs font-semibold text-foreground">Teams and members</h4>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {coverage.map((team) => (
          <div key={team.teamName} className="rounded-lg border border-border p-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-semibold text-foreground">{team.teamName}</span>
              <span className="text-[11px] tabular-nums text-muted-foreground">
                {team.prCount} of {totalPrCount} PRs · {formatShare(team.prCount, totalPrCount)}
              </span>
            </div>
            <ul className="mt-2 space-y-0.5">
              {team.members.map((member) => (
                <li key={member} className="text-[11px] text-muted-foreground">
                  {member}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground">
        <span className="tabular-nums">{memberCount}</span> people ·{' '}
        <span className="tabular-nums">
          {attributed} of {totalPrCount}
        </span>{' '}
        PRs · {formatShare(attributed, totalPrCount)} of the pull requests merged in{' '}
        <code>{NORDIC_REVIEW_REPO}</code> in this period. The remainder are from people outside
        these three teams, and from bots.
      </p>
    </div>
  );
}
